import { useState, useEffect, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Shield, Landmark, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useFeatureMode, isFinancialExecutionAllowed } from "../hooks/useFeatureMode";
import SafetyFreezeNotice from "./SafetyFreezeNotice";

/**
 * Inline BACS Direct Debit mandate confirmation for regular-giving donations
 * routed to a charity's Stripe Connect account.
 *
 * The donor's bank details (already captured in the form) are passed directly
 * to Stripe.js confirmBacsDebitSetup — they never touch our backend. The
 * recurring subscription is created server-side on setup_intent.succeeded.
 */
export const BacsMandateConfirm = ({
  config,            // { clientSecret, publishableKey, stripeAccount, setupIntentId, amount, currency }
  conversionId,
  sortCode,
  accountNumber,
  accountHolderName,
  email,
  ddGuaranteeText,
  onConfirmed,
  onSkip,
}) => {
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const guaranteeEmitted = useRef(false);
  const featureMode = useFeatureMode();
  const financialExecutionAllowed = isFinancialExecutionAllowed(featureMode);

  // Bank details were captured in the preceding form step.
  useEffect(() => {
    if (conversionId && financialExecutionAllowed) {
      apiClient.post("/donations/bacs/bank-details-captured", { conversionId }).catch(() => {});
    }
  }, [conversionId, financialExecutionAllowed]);

  // Package 0 Safety Freeze — BACS mandate flow disabled in statements-only mode.
  if (!financialExecutionAllowed) {
    return (
      <div data-testid="bacs-mandate-confirm-disabled">
        <SafetyFreezeNotice
          title="Direct Debit setup is not part of the current release"
          message="Donor Direct Debit mandates are handled outside OCI Engage in the current release. Donor submissions are recorded securely for the beneficiary charity."
          testId="bacs-mandate-safety-notice"
        />
        {typeof onSkip === "function" && (
          <Button variant="ghost" onClick={onSkip} data-testid="bacs-mandate-skip-btn">
            Continue
          </Button>
        )}
      </div>
    );
  }

  const handleAccept = (checked) => {
    setAccepted(checked);
    if (checked && !guaranteeEmitted.current && conversionId) {
      guaranteeEmitted.current = true;
      apiClient.post("/donations/bacs/guarantee-accepted", { conversionId }).catch(() => {});
    }
  };

  const handleConfirm = async () => {
    if (!accepted || !config?.clientSecret || !config?.publishableKey) return;
    setSubmitting(true);
    try {
      const stripe = await loadStripe(
        config.publishableKey,
        config.stripeAccount ? { stripeAccount: config.stripeAccount } : undefined
      );
      if (!stripe) {
        toast.error("Could not load secure payment library. Please try again.");
        setSubmitting(false);
        return;
      }

      const { error, setupIntent } = await stripe.confirmBacsDebitSetup(config.clientSecret, {
        payment_method: {
          bacs_debit: {
            sort_code: (sortCode || "").replace(/[-\s]/g, ""),
            account_number: (accountNumber || "").replace(/\s/g, ""),
          },
          billing_details: {
            name: accountHolderName || "",
            email: email && email.includes("@") ? email : undefined,
          },
        },
      });

      if (error) {
        toast.error(error.message || "Direct Debit confirmation failed");
        setSubmitting(false);
        return;
      }

      await apiClient
        .post("/donations/bacs/confirmed", {
          conversionId,
          setupIntentId: setupIntent?.id || config.setupIntentId || null,
        })
        .catch(() => {});

      setDone(true);
      toast.success("Direct Debit mandate confirmed — regular giving is being set up.");
      setTimeout(() => onConfirmed && onConfirmed(), 900);
    } catch (e) {
      toast.error("Something went wrong confirming the Direct Debit. Please try again.");
      setSubmitting(false);
    }
  };

  const amountLabel = config?.amount
    ? `£${Number(config.amount).toFixed(2)}/month`
    : "your regular donation";

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-lg"
        data-testid="bacs-mandate-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Landmark className="h-5 w-5 text-teal-600" />
            Confirm your Direct Debit
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="py-10 flex flex-col items-center text-center" data-testid="bacs-mandate-success">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mb-3" />
            <p className="text-base font-semibold text-slate-800">Mandate confirmed</p>
            <p className="text-sm text-slate-500 mt-1">Setting up your regular giving…</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Regular gift</span>
                <span className="font-semibold text-slate-800" data-testid="bacs-amount">{amountLabel}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-500">Account holder</span>
                <span className="font-medium text-slate-700">{accountHolderName || "—"}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-500">Account</span>
                <span className="font-mono text-slate-700">
                  ••-••-{(sortCode || "").replace(/[-\s]/g, "").slice(-2)} / ••••{(accountNumber || "").slice(-4)}
                </span>
              </div>
            </div>

            <div className="rounded-lg border border-teal-100 bg-teal-50/60 p-3">
              <div className="flex items-center gap-2 text-teal-800 font-semibold text-sm mb-1">
                <Shield className="h-4 w-4" /> The Direct Debit Guarantee
              </div>
              <p className="text-xs leading-relaxed text-slate-600 whitespace-pre-line" data-testid="bacs-guarantee-text">
                {ddGuaranteeText ||
                  "This Guarantee is offered by all banks and building societies that accept instructions to pay Direct Debits. If there are any changes to the amount, date or frequency of your Direct Debit the organisation will notify you in advance. If an error is made in the payment of your Direct Debit, you are entitled to a full and immediate refund from your bank or building society. You can cancel a Direct Debit at any time by contacting your bank or building society."}
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={accepted}
                onCheckedChange={handleAccept}
                data-testid="bacs-guarantee-checkbox"
                className="mt-0.5"
              />
              <span className="text-sm text-slate-700">
                I confirm I am the account holder and the only person required to authorise Direct
                Debits on this account, and I accept the Direct Debit Guarantee.
              </span>
            </label>

            <div className="flex gap-2 pt-1">
              {onSkip && (
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={onSkip}
                  disabled={submitting}
                  data-testid="bacs-skip-button"
                >
                  Not now
                </Button>
              )}
              <Button
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                onClick={handleConfirm}
                disabled={!accepted || submitting}
                data-testid="bacs-confirm-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Confirming…
                  </>
                ) : (
                  "Confirm Direct Debit"
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BacsMandateConfirm;
